import { differenceInSeconds } from "date-fns";
import DOMPurify from "dompurify";
import { useEffect, useRef, useState } from "react";
import type { Agent } from "../../common/types/Agent";
import type { AiSummary } from "../../common/types/AiSummary";
import { EventEmitter, type EventListener } from "../../EventEmitter";
import { formatChatMessageDate } from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import "./AiSummaryDisplay.css";

type Props = {
    aiSummary: AiSummary;
    agents?: Agent[];
    isLatest?: boolean;
};

function resolveAgentName(agentId: string, agents?: Agent[]): string {
    if (!agents) {
        return "AI Assistant";
    }

    const agent = agents.find((agent) => agent.id === agentId);
    return agent?.name || "AI Assistant";
}

function AiSummaryDisplay({ aiSummary, agents, isLatest = false }: Props) {
    const [isCollapsed, setIsCollapsed] = useState(!isLatest);
    const summaryContentRef = useRef<HTMLDivElement>(null);

    // Listen for new AI summary events to collapse old summaries
    useEffect(() => {
        const collapseOldSummariesListener: EventListener = {
            name: "collapseOldAiSummaries",
            listener: () => {
                const now = new Date();
                const summaryDate = fromTimestampToDate(aiSummary.sentAt);
                const summaryAgeSeconds = differenceInSeconds(now, summaryDate);

                // If this summary is older than 5 seconds, collapse it
                if (summaryAgeSeconds > 5) {
                    setIsCollapsed(true);
                }
            },
        };

        EventEmitter.addListener(collapseOldSummariesListener);
        return () => {
            EventEmitter.removeListener(collapseOldSummariesListener);
        };
    }, [aiSummary.sentAt]);

    // Handle sanitized HTML rendering using RETURN_DOM for better XSS protection
    useEffect(() => {
        // Only populate content when expanded and content exists
        if (summaryContentRef.current && aiSummary.summaryContent && !isCollapsed) {
            // Clear previous content
            summaryContentRef.current.innerHTML = "";

            // Use DOMPurify with RETURN_DOM for better XSS protection
            const sanitizedFragment = DOMPurify.sanitize(aiSummary.summaryContent, {
                RETURN_DOM: true,
            });

            // Append the sanitized DocumentFragment to the ref element
            summaryContentRef.current.appendChild(sanitizedFragment);
        }
    }, [aiSummary.summaryContent, isCollapsed]);

    return (
        <div className="generated-options-selector">
            <Flex.Column gap="large">
                <Flex.Row
                    className="generated-options-selector__header"
                    justifyContent="space-between"
                    onClick={() => {
                        setIsCollapsed(!isCollapsed);
                    }}
                >
                    <Label.Medium.N50>
                        AI Summary{" "}
                        {aiSummary.summaryType === "request"
                            ? "(Request-specific)"
                            : "(Conversation)"}
                    </Label.Medium.N50>
                    {isCollapsed ? (
                        <Icon.UnfoldMore.Medium />
                    ) : (
                        <Icon.UnfoldLess.Medium />
                    )}
                </Flex.Row>
                {!isCollapsed && (
                    <div className="generated-ai-summary__content">
                        <Flex.Column gap="large">
                            <div
                                className="generated-ai-summary__text"
                                ref={summaryContentRef}
                            />
                            <div className="chat-message__author-row__sender">
                                {formatChatMessageDate(aiSummary.sentAt)}
                                <InlineTextDelimiter />
                                {resolveAgentName(aiSummary.createdBy, agents)}
                            </div>
                        </Flex.Column>
                    </div>
                )}
            </Flex.Column>
        </div>
    );
}

export default AiSummaryDisplay;
